import axios from "axios";
import { baseUrl } from "../baseUrl/BaseUrl";

// ─── Flag anti-boucle de refresh ─────────────────────────────────────────────
let isRefreshing = false;
// File d'attente des requêtes en attente d'un nouveau token
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(({ resolve, reject }) => {
        if (error) {
            reject(error);
        } else {
            resolve(token);
        }
    });
    failedQueue = [];
};

// ─── Factory d'instance Axios ─────────────────────────────────────────────────
const axiosInstance = (accessToken) => {
    // On n'injecte le token dans le header initial QUE s'il est explicitement passé.
    // Les routes publiques (login, sliders…) n'ont JAMAIS de token = comportement original.
    const authorization = accessToken ? `Bearer ${accessToken}` : "";

    const instance = axios.create({
        baseURL: baseUrl,
        withCredentials: false,
        headers: {
            Authorization: authorization,
        },
    });

    // ── Intercepteur REQUEST ──────────────────────────────────────────────────
    // Pour les routes protégées (non-public) : injecte toujours le token le plus récent.
    // Pour les routes publiques (public/*) : supprime le header Authorization.
    instance.interceptors.request.use(
        (config) => {
            const isPublicRoute = config.url?.startsWith('public/');
            if (!isPublicRoute) {
                const currentToken = sessionStorage.getItem('accessTokenU');
                if (currentToken) {
                    config.headers['Authorization'] = `Bearer ${currentToken}`;
                }
            } else {
                // Routes publiques : jamais de token pour ne pas bloquer login, sliders, etc.
                delete config.headers['Authorization'];
            }
            return config;
        },
        (error) => Promise.reject(error)
    );

    // ── Intercepteur RESPONSE : gère les 401 ─────────────────────────────────
    instance.interceptors.response.use(
        (response) => response,
        async (error) => {
            const originalRequest = error.config;

            // Ne s'applique qu'aux routes protégées qui retournent 401
            if (
                error.response?.status === 401 &&
                !originalRequest._retry &&
                !originalRequest.url?.includes('api/auth/refresh') &&
                !originalRequest.url?.startsWith('public/')
            ) {
                if (isRefreshing) {
                    return new Promise((resolve, reject) => {
                        failedQueue.push({ resolve, reject });
                    }).then((token) => {
                        originalRequest.headers['Authorization'] = `Bearer ${token}`;
                        return axios(originalRequest);
                    }).catch((err) => Promise.reject(err));
                }

                originalRequest._retry = true;
                isRefreshing = true;

                try {
                    // Import dynamique pour éviter les dépendances circulaires
                    const { default: store } = await import('../store/store');
                    const { refreshUserToken } = await import('../features/authSliceUser');

                    const newToken = await store.dispatch(refreshUserToken());

                    if (newToken) {
                        processQueue(null, newToken);
                        originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                        return axios(originalRequest);
                    } else {
                        processQueue(new Error('Refresh échoué'), null);
                        return Promise.reject(error);
                    }
                } catch (refreshError) {
                    processQueue(refreshError, null);
                    return Promise.reject(refreshError);
                } finally {
                    isRefreshing = false;
                }
            }

            return Promise.reject(error);
        }
    );

    return instance;
};

export default axiosInstance;